import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { User, MapPin, Heart, Award, Sparkles, Star, Globe, ArrowRight, BookOpen, LogIn, LogOut, Cloud, ExternalLink, Trash2 } from 'lucide-react';
import TopBar from '@/components/TopBar';
import PassportMap from '@/components/PassportMap';
import PassportSouvenirs from '@/components/PassportSouvenirs';
import EmailAuthForm from '@/components/EmailAuthForm';
import { useCity } from '@/context/CityContext';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';

function loadVisited() {
  try { return JSON.parse(localStorage.getItem('gt_visited') || '[]'); } catch { return []; }
}
function loadFavorites() {
  try { return JSON.parse(localStorage.getItem('puvio_favorites') || '[]'); } catch { return []; }
}

export default function Profile() {
  const { city } = useCity();
  const navigate = useNavigate();
  const { user, loading: authLoading, login, logout } = useAuth();
  const [visited, setVisited] = useState([]);
  const [favs, setFavs] = useState([]);
  const [savedPlans, setSavedPlans] = useState([]);

  useEffect(() => {
    const arr = loadVisited();
    // Add current city if missing
    if (city?.name && !arr.find(c => c.name === city.name && c.country === city.country)) {
      arr.push({ name: city.name, country: city.country, country_code: city.country_code, ts: new Date().toISOString() });
      try { localStorage.setItem('gt_visited', JSON.stringify(arr.slice(-50))); } catch {}
    }
    setVisited(arr);
    setFavs(loadFavorites());
    try { setSavedPlans(JSON.parse(localStorage.getItem('gt_my_plans') || '[]')); } catch { setSavedPlans([]); }
  }, [city?.name, city?.country]);

  const removePlan = (sid) => {
    const next = savedPlans.filter(p => p.sid !== sid);
    setSavedPlans(next);
    try { localStorage.setItem('gt_my_plans', JSON.stringify(next)); } catch {}
  };

  const stats = useMemo(() => {
    const countries = new Set(visited.map(v => v.country).filter(Boolean));
    return [
      { label: 'Cities',    value: visited.length, icon: MapPin, tint: 'from-blue-500/30 to-cyan-400/20' },
      { label: 'Countries', value: countries.size, icon: Globe,  tint: 'from-violet-500/30 to-indigo-500/20' },
      { label: 'Saved',     value: favs.length,    icon: Heart,  tint: 'from-rose-500/30 to-pink-400/20' },
    ];
  }, [visited, favs]);

  const badges = useMemo(() => {
    const list = [];
    if (visited.length >= 1) list.push({ id: 'first', label: 'First check-in', icon: Sparkles });
    if (visited.length >= 5) list.push({ id: 'explorer', label: 'Explorer', icon: Award });
    if (visited.length >= 10) list.push({ id: 'globetrotter', label: 'Globetrotter', icon: Globe });
    if (favs.length >= 3)    list.push({ id: 'collector', label: 'Collector', icon: Star });
    return list;
  }, [visited, favs]);

  return (
    <div data-testid="profile-screen">
      <TopBar />

      <div className="px-5 pt-1">
        <div className="flex items-center gap-3">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name || 'User avatar'}
              referrerPolicy="no-referrer"
              data-testid="profile-avatar"
              className="w-14 h-14 rounded-2xl object-cover shadow-[0_10px_28px_rgba(15,26,19,0.18)] border border-[#0F1A13]/10"
            />
          ) : (
            <div className="w-14 h-14 rounded-2xl bg-[#15291C] flex items-center justify-center shadow-[0_10px_28px_rgba(15,26,19,0.18)]" data-testid="profile-avatar-placeholder">
              <User className="w-6 h-6 text-[#C4FF3D]" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#15291C]">
              {user ? 'Signed in' : 'Traveler'}
            </p>
            <h1 className="font-display text-2xl font-bold tracking-tight text-[#0F1A13] truncate" data-testid="profile-name">
              {user?.name || 'Your journey'}
            </h1>
            {user?.email ? (
              <p className="text-[12px] text-[#7A8A80] truncate" data-testid="profile-email">{user.email}</p>
            ) : null}
          </div>
        </div>
      </div>

      {/* Auth card */}
      <div className="px-5 mt-4">
        {authLoading ? (
          <div className="glass-card p-4 text-[12px] text-[#7A8A80]" data-testid="profile-auth-loading">
            Checking sign-in…
          </div>
        ) : user ? (
          <div className="glass-card p-3 flex items-center gap-3" data-testid="profile-auth-signedin">
            <div className="w-9 h-9 rounded-xl bg-[#C4FF3D]/30 border border-[#15291C]/15 flex items-center justify-center">
              <Cloud className="w-4 h-4 text-[#15291C]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[#0F1A13] font-semibold text-sm">Cloud sync on</p>
              <p className="text-[#7A8A80] text-[12px] truncate">Favorites, visited & diary saved to your account</p>
            </div>
            <button
              type="button"
              onClick={logout}
              data-testid="profile-logout-btn"
              className="gt-pill gt-pill-light text-[12px] !py-1.5 !px-3 inline-flex items-center gap-1.5"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {/* Default: email + password */}
            <EmailAuthForm />

            {/* Divider */}
            <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7A8A80] my-1">
              <span className="flex-1 h-px bg-[#0F1A13]/10" />
              or
              <span className="flex-1 h-px bg-[#0F1A13]/10" />
            </div>

            {/* Google Auth — secondary */}
            <button
              type="button"
              onClick={login}
              data-testid="profile-login-btn"
              className="w-full glass-card glass-card-interactive p-3 flex items-center gap-3 text-left"
            >
              <div className="w-9 h-9 rounded-xl bg-[#15291C] flex items-center justify-center">
                <LogIn className="w-4 h-4 text-[#C4FF3D]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#0F1A13] font-semibold text-sm">Continue with Google</p>
                <p className="text-[#7A8A80] text-[12px]">One-tap sign-in via Emergent Auth</p>
              </div>
              <ArrowRight className="w-4 h-4 text-[#15291C]" />
            </button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="px-5 mt-5 grid grid-cols-3 gap-3" data-testid="profile-stats">
        {stats.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.05 }}
            className="glass-card p-4"
            data-testid={`profile-stat-${s.label.toLowerCase()}`}
          >
            <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${s.tint} border border-[#0F1A13]/10 flex items-center justify-center mb-2`}>
              <s.icon className="w-4 h-4 text-[#15291C]" />
            </div>
            <p className="font-display text-2xl font-bold text-[#0F1A13]">{s.value}</p>
            <p className="text-[10.5px] uppercase tracking-wider text-[#4A5A50] font-semibold mt-0.5">{s.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Badges */}
      <div className="px-5 mt-5">
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-[#15291C] font-bold mb-2">Badges</p>
        {badges.length === 0 ? (
          <div className="glass-card p-4 text-[#4A5A50] text-sm" data-testid="profile-badges-empty">
            Visit your first city to earn your first badge.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="profile-badges">
            {badges.map(b => (
              <span key={b.id} className="gt-pill gt-pill-accent text-[11.5px]" data-testid={`profile-badge-${b.id}`}>
                <b.icon className="w-3.5 h-3.5" /> {b.label}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Visited cities */}
      <div className="px-5 mt-5 pb-2">
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-[#15291C] font-bold mb-2">Visited cities</p>
        {visited.length === 0 ? (
          <div className="glass-card p-4 text-[#4A5A50] text-sm">No cities yet — start exploring.</div>
        ) : (
          <div className="flex flex-col gap-2" data-testid="profile-visited">
            {visited.slice().reverse().slice(0, 12).map((v, i) => (
              <div key={`${v.name}-${i}`} className="glass-card p-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#C4FF3D]/30 border border-[#15291C]/15 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#15291C]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#0F1A13] font-semibold text-sm truncate">{v.name}</p>
                  <p className="text-[#7A8A80] text-[12px] truncate">{v.country || '—'}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Passport souvenirs — flags + achievements */}
      <PassportSouvenirs />

      {/* Saved itineraries */}
      <div className="px-5 mt-5 pb-2">
        <p className="text-[10.5px] uppercase tracking-[0.22em] text-[#15291C] font-bold mb-2">Saved itineraries</p>
        {savedPlans.length === 0 ? (
          <div className="glass-card p-4 text-[12.5px] text-[#4A5A50]" data-testid="profile-saved-empty">
            No saved itineraries yet — ask TrivlyAI to plan a trip and tap "Save & share".
          </div>
        ) : (
          <div className="flex flex-col gap-2" data-testid="profile-saved-list">
            {savedPlans.slice(0, 8).map((p, i) => (
              <div key={p.sid || i} className="glass-card p-3 flex items-center gap-3" data-testid={`profile-saved-${i}`}>
                <div className="w-9 h-9 rounded-xl bg-[#15291C] flex items-center justify-center shrink-0">
                  <BookOpen className="w-4 h-4 text-[#C4FF3D]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[#0F1A13] font-semibold text-[13.5px] truncate">{p.title || 'Untitled plan'}</p>
                  <p className="text-[#7A8A80] text-[11px] truncate">{p.city_name}{p.country ? ` · ${p.country}` : ''}</p>
                </div>
                <button type="button" onClick={() => window.open(`/share/${p.sid}`, '_blank')} aria-label="Open" className="text-[#15291C] hover:text-[#0F1A13]">
                  <ExternalLink className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => removePlan(p.sid)} aria-label="Remove" className="text-[#7A8A80] hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI nudge */}
      <div className="px-5 mt-3 pb-2 flex flex-col gap-2">
        <button
          type="button" onClick={() => navigate('/diary')}
          className="w-full glass-card glass-card-interactive p-4 flex items-center gap-3 text-left"
          data-testid="profile-diary-cta"
        >
          <div className="w-10 h-10 rounded-xl bg-[#15291C] flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-[#C4FF3D]" />
          </div>
          <div className="flex-1">
            <p className="text-[#0F1A13] font-semibold text-sm">Trip diary</p>
            <p className="text-[#7A8A80] text-[12px]">Capture tiny moments worth keeping</p>
          </div>
          <ArrowRight className="w-4 h-4 text-[#15291C]" />
        </button>
        <button
          type="button" onClick={() => navigate('/ai')}
          className="w-full glass-card glass-card-interactive p-4 flex items-center gap-3 text-left"
          data-testid="profile-ai-cta"
        >
          <div className="w-10 h-10 rounded-xl bg-[#15291C] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#C4FF3D]" />
          </div>
          <div className="flex-1">
            <p className="text-[#0F1A13] font-semibold text-sm">Ask the AI for personalized picks</p>
            <p className="text-[#7A8A80] text-[12px]">Tailored to where you've been</p>
          </div>
          <ArrowRight className="w-4 h-4 text-[#15291C]" />
        </button>
      </div>

      {/* Passport map */}
      <div className="px-5 mt-3 pb-2">
        <PassportMap visitedCountryCodes={Array.from(new Set(visited.map(v => v.country_code).filter(Boolean)))} />
      </div>

      <div className="px-5 py-6 text-center" data-testid="profile-footer">
        <p className="text-[12px] font-bold text-[#0F1A13] font-display" data-testid="footer-brand">Trivly</p>
        <p className="text-[11px] text-[#7A8A80] mt-1">Plan less. Experience more.</p>
        <div className="mt-3 flex items-center justify-center gap-3 text-[11px] font-semibold text-[#4A5A50]">
          <a href="/legal/privacy" className="hover:text-[#15291C] underline-offset-2 hover:underline">Privacy</a>
          <span aria-hidden="true">·</span>
          <a href="/legal/terms" className="hover:text-[#15291C] underline-offset-2 hover:underline">Terms</a>
          <span aria-hidden="true">·</span>
          <a href="https://www.nevika.co" target="_blank" rel="noreferrer" className="hover:text-[#15291C] underline-offset-2 hover:underline">nevika.co</a>
        </div>
        <div className="mt-4 mx-auto max-w-[320px] rounded-2xl border border-[#0F1A13]/10 bg-white/60 px-4 py-3 text-[10.5px] text-[#4A5A50] leading-relaxed" data-testid="profile-corporate">
          <p className="text-[9.5px] uppercase tracking-[0.24em] font-bold text-[#15291C]">A Nevika Innovations product</p>
          <p className="mt-1.5 font-semibold text-[#0F1A13] text-[11px]">Nevika Innovations Private Limited</p>
          <p className="text-[10px] italic text-[#7A8A80]">Infrastructure for the Future</p>
          <p className="mt-2">1st Floor, Mapkhan Nagar, Marol Naka,<br />Andheri (E), Mumbai – 400059, India</p>
          <p className="mt-1.5">
            <span className="font-semibold text-[#0F1A13]">CIN</span> U62090MH2026OPC472128 ·{' '}
            <span className="font-semibold text-[#0F1A13]">GSTIN</span> 27AALCN7118G1Z9
          </p>
          <p className="mt-1.5">
            <a className="hover:underline" href="mailto:info@nevika.co">info@nevika.co</a> ·{' '}
            <a className="hover:underline" href="tel:+918291998800">+91 82919 98800</a>
          </p>
        </div>
        <p className="text-[10px] text-[#A8B5AB] mt-3">© {new Date().getFullYear()} Nevika Innovations Private Limited</p>
      </div>
    </div>
  );
}
